import { z } from "zod";
import { uuidSchema } from "./aiTypes.ts";

export const PROVIDER_TYPES = ["openai", "anthropic", "gemini", "openrouter", "custom"] as const;
export const providerTypeSchema = z.enum(PROVIDER_TYPES);
export type ProviderType = z.infer<typeof providerTypeSchema>;

export const encryptedEnvelopeSchema = z.strictObject({
  encryptedKey: z.string().min(1),
  iv: z.string().min(1),
  ciphertext: z.string().min(1),
});
export type EncryptedEnvelope = z.infer<typeof encryptedEnvelopeSchema>;

export const provisioningPayloadSchema = z.strictObject({
  machineId: uuidSchema,
  credentialId: z.string().min(1).max(64),
  provider: providerTypeSchema,
  apiKey: z.string().min(1).max(4096),
  issuedAt: z.number().int().positive(),
  expiresAt: z.number().int().positive(),
  nonce: z.string().min(16).max(128),
});
export type ProvisioningPayload = z.infer<typeof provisioningPayloadSchema>;

export const credentialAvailabilitySchema = z.strictObject({
  machineId: uuidSchema,
  machineName: z.string(),
  online: z.boolean(),
  configured: z.boolean(),
  lastTestedAt: z.string().optional(),
  testStatus: z.enum(["ok", "failed"]).optional(),
  testError: z.string().optional(),
  lastSuccessfulUseAt: z.string().optional(),
});
export type CredentialAvailability = z.infer<typeof credentialAvailabilitySchema>;

export const credentialMetadataSchema = z.strictObject({
  id: z.string().min(1).max(64),
  provider: providerTypeSchema,
  label: z.string().min(1).max(100),
  baseUrl: z.string().url().optional(),
  machineAvailability: z.array(credentialAvailabilitySchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CredentialMetadata = z.infer<typeof credentialMetadataSchema>;

export const modelDefaultRoleSchema = z.enum(["chat", "agent", "autocomplete", "ask", "plan", "edit"]);
export type ModelDefaultRole = z.infer<typeof modelDefaultRoleSchema>;

export const modelDefaultSchema = z.strictObject({
  role: modelDefaultRoleSchema,
  provider: z.string().min(1).max(64),
  credentialId: z.string().min(1).max(64).optional(),
  model: z.string().min(1).max(200),
  effort: z.enum(["low", "medium", "high"]).optional(),
});
export type ModelDefault = z.infer<typeof modelDefaultSchema>;

export const projectModelDefaultsSchema = z.strictObject({
  projectId: z.string().optional(),
  defaults: z.array(modelDefaultSchema),
});
export type ProjectModelDefaults = z.infer<typeof projectModelDefaultsSchema>;

export const usageLimitScopeSchema = z.enum(["account", "project", "credential"]);
export type UsageLimitScope = z.infer<typeof usageLimitScopeSchema>;

export const usageLimitSchema = z.strictObject({
  scopeType: usageLimitScopeSchema,
  scopeId: z.string().max(128).optional(),
  warningCostUsd: z.number().nonnegative().optional(),
  hardCostUsd: z.number().nonnegative().optional(),
  warningTokens: z.number().int().nonnegative().optional(),
  hardTokens: z.number().int().nonnegative().optional(),
});
export type UsageLimit = z.infer<typeof usageLimitSchema>;

export const monthlyUsageStatusSchema = z.strictObject({
  scopeType: usageLimitScopeSchema,
  scopeId: z.string().max(128).optional(),
  period: z.string(),
  currentCostUsd: z.number().nonnegative(),
  currentTokens: z.number().int().nonnegative(),
  costIncomplete: z.boolean(),
  warningCostUsd: z.number().nonnegative().optional(),
  hardCostUsd: z.number().nonnegative().optional(),
  warningTokens: z.number().int().nonnegative().optional(),
  hardTokens: z.number().int().nonnegative().optional(),
  warningTriggered: z.boolean(),
  hardLimitExceeded: z.boolean(),
});
export type MonthlyUsageStatus = z.infer<typeof monthlyUsageStatusSchema>;

function toBase64(buf: ArrayBuffer | Uint8Array): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(buf instanceof Uint8Array ? buf : new Uint8Array(buf)).toString("base64");
  }
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
  if (typeof Buffer !== "undefined") {
    const buf = Buffer.from(b64, "base64");
    const ab = new ArrayBuffer(buf.byteLength);
    new Uint8Array(ab).set(buf);
    return new Uint8Array(ab);
  }
  const binary = atob(b64);
  const ab = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(ab);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Standard hybrid encryption (RSA-OAEP-SHA256 + AES-256-GCM).
 * Supported natively in both browser (window.crypto.subtle) and Node.js (globalThis.crypto.subtle).
 */
export async function encryptCredentialEnvelope(
  publicKeySpkiBase64: string,
  payload: ProvisioningPayload,
): Promise<EncryptedEnvelope> {
  const subtle = globalThis.crypto.subtle;
  const spkiBytes = fromBase64(publicKeySpkiBase64);
  const rsaKey = await subtle.importKey(
    "spki",
    spkiBytes.buffer as ArrayBuffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );

  const aesKey = await subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt"]);
  const rawAesKey = await subtle.exportKey("raw", aesKey);

  const encryptedKey = await subtle.encrypt(
    { name: "RSA-OAEP" },
    rsaKey,
    rawAesKey,
  );

  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const encodedPayload = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = await subtle.encrypt(
    { name: "AES-GCM", iv: iv.buffer },
    aesKey,
    encodedPayload.buffer,
  );

  return {
    encryptedKey: toBase64(encryptedKey),
    iv: toBase64(iv),
    ciphertext: toBase64(ciphertext),
  };
}

/**
 * Decrypts a provisioning envelope using the Agent's RSA-OAEP private key.
 */
export async function decryptCredentialEnvelope(
  privateKeyPkcs8Base64: string,
  envelope: EncryptedEnvelope,
): Promise<ProvisioningPayload> {
  const subtle = globalThis.crypto.subtle;
  const pkcs8Bytes = fromBase64(privateKeyPkcs8Base64);
  const rsaKey = await subtle.importKey(
    "pkcs8",
    pkcs8Bytes.buffer as ArrayBuffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["decrypt"],
  );

  const encryptedKeyBytes = fromBase64(envelope.encryptedKey);
  const rawAesKey = await subtle.decrypt(
    { name: "RSA-OAEP" },
    rsaKey,
    encryptedKeyBytes.buffer as ArrayBuffer,
  );

  const aesKey = await subtle.importKey(
    "raw",
    rawAesKey,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );
  const iv = fromBase64(envelope.iv);
  const ciphertextBytes = fromBase64(envelope.ciphertext);

  const decryptedBytes = await subtle.decrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    aesKey,
    ciphertextBytes.buffer as ArrayBuffer,
  );
  const jsonStr = new TextDecoder().decode(decryptedBytes);
  const parsed = JSON.parse(jsonStr) as unknown;
  return provisioningPayloadSchema.parse(parsed);
}

export interface EnvelopeValidationExpected {
  machineId: string;
  credentialId: string;
  provider: ProviderType;
  now?: number;
  seenNonces: Set<string> | Map<string, number>;
}

export function validateProvisioningEnvelope(
  payload: ProvisioningPayload,
  expected: EnvelopeValidationExpected,
): { ok: true } | { ok: false; error: string } {
  const now = expected.now ?? Date.now();

  if (payload.machineId !== expected.machineId) {
    return { ok: false, error: "machine ID mismatch" };
  }
  if (payload.credentialId !== expected.credentialId) {
    return { ok: false, error: "credential ID mismatch" };
  }
  if (payload.provider !== expected.provider) {
    return { ok: false, error: "provider type mismatch" };
  }
  if (now > payload.expiresAt) {
    return { ok: false, error: "envelope has expired" };
  }
  if (now < payload.issuedAt - 60_000) {
    return { ok: false, error: "envelope issued in the future" };
  }
  if (expected.seenNonces instanceof Set) {
    if (expected.seenNonces.has(payload.nonce)) {
      return { ok: false, error: "envelope nonce already used" };
    }
    expected.seenNonces.add(payload.nonce);
  } else {
    if (expected.seenNonces.has(payload.nonce)) {
      return { ok: false, error: "envelope nonce already used" };
    }
    expected.seenNonces.set(payload.nonce, now);
  }
  return { ok: true };
}
